import type { ProblemDetail } from '@/types/api'
import { clearSession, establishSession } from '@/features/auth/sessionCache'
import { appQueryClient } from '@/lib/queryClient'
import { getAccessToken } from '@/store/session'

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1'

// Rutas de autenticacion: un 401 aqui es "credenciales invalidas" o "sesion
// vencida", nunca "access token expirado", asi que nunca disparan el
// refresco automatico de abajo (evitaria un bucle infinito).
const AUTH_PATHS = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/logout', '/auth/verify-email', '/auth/request-password-reset', '/auth/reset-password']

let refreshPromise: Promise<string | null> | null = null

/**
 * Renueva el access token usando la cookie HttpOnly de refresh. Deduplicada:
 * si varias peticiones reciben un 401 a la vez, solo una llama a /auth/refresh
 * y el resto espera la misma promesa (evita disparar N refrescos).
 */
async function refreshAccessToken(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = fetch(`${BASE_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'application/json', 'X-Requested-With': 'BsPOS' },
    })
      .then(async (response) => {
        if (!response.ok) {
          await clearSession(appQueryClient)
          return null
        }
        const body = await response.json()
        await establishSession(appQueryClient, body.user, body.accessToken)
        return body.accessToken as string
      })
      .catch(async () => {
        await clearSession(appQueryClient)
        return null
      })
      .finally(() => {
        refreshPromise = null
      })
  }
  return refreshPromise
}

/**
 * Error de API con el cuerpo RFC 7807 ya interpretado, para que las pantallas
 * muestren el mensaje del servidor en vez de un "algo salio mal".
 */
export class ApiError extends Error {
  readonly status: number
  readonly problem: ProblemDetail | null

  constructor(status: number, problem: ProblemDetail | null, fallback: string) {
    super(problem?.detail || problem?.title || fallback)
    this.name = 'ApiError'
    this.status = status
    this.problem = problem
  }
}

function buildUrl(path: string, params?: Record<string, unknown>): string {
  const url = `${BASE_URL}${path}`
  if (!params) return url

  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      search.append(key, String(value))
    }
  }
  const query = search.toString()
  return query ? `${url}?${query}` : url
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  params?: Record<string, unknown>
  body?: unknown
}

async function doFetch(path: string, options: RequestOptions): Promise<Response> {
  const { params, body, headers, ...rest } = options
  const accessToken = getAccessToken()

  return fetch(buildUrl(path, params), {
    ...rest,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'BsPOS',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...headers,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  })
}

function parsePayload(text: string): { valid: boolean; value: unknown } {
  if (!text) return { valid: true, value: null }
  try {
    return { valid: true, value: JSON.parse(text) }
  } catch {
    return { valid: false, value: null }
  }
}

async function fetchResponse(path: string, options: RequestOptions): Promise<Response> {
  try {
    return await doFetch(path, options)
  } catch {
    throw new ApiError(0, null, 'No pudimos conectar con el servidor. Revisa tu conexión e inténtalo de nuevo.')
  }
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let response = await fetchResponse(path, options)

  if (response.status === 401 && !AUTH_PATHS.some((authPath) => path.startsWith(authPath))) {
    const newToken = await refreshAccessToken()
    if (newToken) {
      response = await fetchResponse(path, options)
    }
  }

  if (response.status === 204) {
    return undefined as T
  }

  const parsed = parsePayload(await response.text())

  if (!response.ok) {
    throw new ApiError(response.status, parsed.value as ProblemDetail | null, 'No pudimos conectar con el servidor.')
  }

  if (!parsed.valid) throw new ApiError(response.status, null, 'El servidor devolvió una respuesta inválida. Inténtalo de nuevo.')

  return parsed.value as T
}

export const api = {
  get: <T,>(path: string, params?: Record<string, unknown>, signal?: AbortSignal) => request<T>(path, { method: 'GET', params, signal }),
  post: <T,>(path: string, body?: unknown, headers?: HeadersInit) => request<T>(path, { method: 'POST', body, headers }),
  put: <T,>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T,>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T,>(path: string) => request<T>(path, { method: 'DELETE' }),
}
